import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { useNavigate } from "react-router-dom";
import { Select, Grid, Checkbox, Popover, Loader } from "@mantine/core";
import { useForm } from "@mantine/form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/sharp-regular-svg-icons";
import { faInfoCircle } from "@fortawesome/sharp-solid-svg-icons";
import { gql } from "@/__generated__";
import Navbar from "@/components/layout/navbar";
import { Toast } from "design-system/src/components/toast";
import { PageFooter } from "@/components/layout/page-footer";
import {
  InitializeReferralInput,
  SubDenialReasonEnum,
  OtherAppealSituations,
  ExpeditedReviewRecommendation,
} from "@/__generated__/graphql";
import {
  Condition,
  isGlp1Condition,
  Medication,
} from "shared/src/data/medication-condition-pairs";
import { FdaEligibilityPopoverContent } from "@/components/fda-eligibility-popover-content/fda-eligibility-popover-content";
import { DenialReason } from "@/data/denial-reasons";
import { shouldShowSpecialSituationsField } from "shared/src/utils/special-situations-utils";
import { SpecialAppealSituationsCard } from "./special-appeal-situations-card";
import { SubDenialReasonField } from "./sub-denial-reason-field";
import { useDelayedLoading } from "shared/src/hooks/use-delayed-loading";

import styles from "./initial-referral-page.module.scss";
import "../../styles/components/landing-page.scss";
import { ReferPatientProgressTracker } from "@/components/refer-patient-progress-tracker";

const INITIALIZE_REFERRAL = gql(`
  mutation InitializeReferral($input: InitializeReferralInput!) {
    initializeReferral(input: $input) {
      appealId
      condition
      medication
      denialReason
    }
  }
`);

const EXPEDITED_REVIEW_RECOMMENDATION_FOR_INPUTS = gql(`
  query ExpeditedReviewRecommendationForInputs($condition: String!, $denialReason: String!) {
    expeditedReviewRecommendationForInputs(condition: $condition, denialReason: $denialReason)
  }
`);

const MEDICATIONS_FOR_REFERRAL = gql(`
  query MedicationsForReferral {
    medicationsForReferral
  }
`);

const CONDITIONS_TREATED_BY_MEDICATION = gql(`
  query ConditionsTreatedByMedication($medication: String!) {
    conditionsTreatedByMedication(medication: $medication)
  }
`);

interface FormValues {
  condition: string;
  medication: string;
  denialReason: string;
  subDenialReasons: SubDenialReasonEnum[];
  meetsFdaGuidelines: boolean;
  isExpeditedReview: boolean;
}

export function InitialReferralPage() {
  const navigate = useNavigate();

  const {
    data: medicationsData,
    loading: medicationsLoading,
    error: medicationsError,
  } = useQuery(MEDICATIONS_FOR_REFERRAL, {
    fetchPolicy: "no-cache",
  });
  const medicationOptions = medicationsData?.medicationsForReferral ?? [];
  const isHppStrensiqEnabled = medicationOptions.includes(Medication.STRENSIQ);

  const [selectedSituations, setSelectedSituations] = useState<
    OtherAppealSituations[]
  >([]);

  const form = useForm<FormValues>({
    initialValues: {
      condition: "",
      medication: "",
      denialReason: "",
      subDenialReasons: [],
      meetsFdaGuidelines: false,
      isExpeditedReview: false,
    },
    validate: {
      condition: (value) => (!value ? "Medical condition is required" : null),
      medication: (value) => (!value ? "Medication is required" : null),
      denialReason: (value) => (!value ? "Denial reason is required" : null),
      meetsFdaGuidelines: (value, values) =>
        isGlp1Condition(values.condition) && !value
          ? "You must confirm the patient meets FDA guidelines for GLP-1"
          : null,
    },
  });

  const medication = form.values.medication;
  const condition = form.values.condition;

  const {
    data: conditionsData,
    loading: conditionsLoading,
    error: conditionsError,
  } = useQuery(CONDITIONS_TREATED_BY_MEDICATION, {
    variables: { medication },
    fetchPolicy: "no-cache",
    skip: !medication,
  });
  const conditionsForMedication = conditionsData?.conditionsTreatedByMedication;

  const showMedicationsLoading = useDelayedLoading(medicationsLoading);
  const showConditionsLoading = useDelayedLoading(conditionsLoading);

  useEffect(() => {
    if (condition && medication) {
      if (
        conditionsError ||
        (conditionsForMedication &&
          !conditionsForMedication.includes(condition))
      ) {
        form.setFieldValue("condition", "");
      }
    }
  }, [condition, medication, conditionsForMedication, conditionsError]);

  const denialReason = form.values.denialReason;
  const hppDenialReasons: string[] = [
    DenialReason.MEDICAL_NECESSITY,
    DenialReason.FORMULARY,
  ];

  useEffect(() => {
    if (
      condition === Condition.HYPOPHOSPHATASIA &&
      denialReason &&
      !hppDenialReasons.includes(denialReason)
    ) {
      form.setFieldValue("denialReason", "");
    }
  }, [condition]);

  useEffect(() => {
    if (!isGlp1Condition(form.values.condition)) {
      form.setFieldValue("meetsFdaGuidelines", false);
    }
  }, [form.values.condition]);

  const userTouchedExpedited = useRef(false);

  const shouldSkipExpeditedQuery = !condition;
  const { data: expeditedData } = useQuery(
    EXPEDITED_REVIEW_RECOMMENDATION_FOR_INPUTS,
    {
      variables: { condition, denialReason: denialReason || "unknown" },
      fetchPolicy: "no-cache",
      skip: shouldSkipExpeditedQuery,
    },
  );

  useEffect(() => {
    if (userTouchedExpedited.current || !expeditedData) {
      return;
    }
    const recommendation = expeditedData.expeditedReviewRecommendationForInputs;
    form.setFieldValue(
      "isExpeditedReview",
      recommendation === ExpeditedReviewRecommendation.Expedited,
    );
  }, [expeditedData]);

  const shouldShowSpecialSituations = shouldShowSpecialSituationsField(
    medication,
    condition,
  );

  useEffect(() => {
    if (!shouldShowSpecialSituations) {
      setSelectedSituations([]);
    }
  }, [shouldShowSpecialSituations]);

  const [initializeReferral, { loading, error: initializeError }] =
    useMutation(INITIALIZE_REFERRAL);

  const toggleSituation = (situation: OtherAppealSituations) => {
    setSelectedSituations((prev) =>
      prev.includes(situation)
        ? prev.filter((s) => s !== situation)
        : [...prev, situation],
    );
  };

  const handleSubmit = async (values: FormValues) => {
    const input: InitializeReferralInput = {
      condition: values.condition,
      medication: values.medication,
      denialReason: values.denialReason,
      subDenialReasons:
        values.subDenialReasons.length > 0
          ? values.subDenialReasons
          : undefined,
      otherAppealSituations:
        selectedSituations.length > 0 ? selectedSituations : undefined,
      isExpeditedReview: values.isExpeditedReview,
    };

    try {
      const { data } = await initializeReferral({ variables: { input } });
      if (data?.initializeReferral) {
        const { appealId } = data.initializeReferral;
        navigate(`/refer/${appealId}/patient-info`);
      }
    } catch {}
  };

  const handleCancel = () => {
    navigate("/");
  };

  const isFormValid = () => {
    const { condition, medication, denialReason, meetsFdaGuidelines } =
      form.values;
    if (!condition || !medication || !denialReason) return false;
    if (isGlp1Condition(condition) && !meetsFdaGuidelines) return false;
    if (conditionsLoading || !!conditionsError || !conditionsForMedication) {
      return false;
    }
    if (!conditionsForMedication.includes(condition)) {
      return false;
    }
    return true;
  };

  const handleFooterSubmit = async () => {
    const validationResult = form.validate();
    if (validationResult.hasErrors) {
      return;
    }
    await handleSubmit(form.getValues());
  };

  const footer = (
    <PageFooter
      secondaryButton={{
        text: "Cancel",
        onClick: handleCancel,
      }}
      primaryButton={{
        text: "Next",
        onClick: handleFooterSubmit,
        disabled: loading || !isFormValid(),
        loading,
      }}
    />
  );

  return (
    <Navbar titleComponent={<ReferPatientProgressTracker />} footer={footer}>
      <div className={styles.pageContainer}>
        <div className={styles.formContainer}>
          <h2 className={styles.formHeader}>Denial Info</h2>
          <div className={styles.formSubheader}>
            <p className={styles.formSubheader__text}>
              Tell us about the denial your patient needs to appeal. Don't see
              {/* oxlint-disable-next-line jsx-a11y/anchor-is-valid */}
              what you're looking for? <a href="#">Click here.</a>
            </p>
          </div>
          <hr className={styles.sectionDivider} />

          <Toast
            variant="error"
            isVisible={
              !!initializeError || !!medicationsError || !!conditionsError
            }
          >
            {initializeError?.message ??
              (medicationsError || conditionsError
                ? "We couldn't load the available medications or conditions. Try again or contact support."
                : null)}
          </Toast>

          <form
            id="initial-referral-form"
            onSubmit={form.onSubmit(handleSubmit)}
          >
            <Grid>
              <Grid.Col>
                <Select
                  label="Medication"
                  placeholder="Look up by name"
                  data-testid="medication-input"
                  searchable
                  withAsterisk
                  disabled={loading}
                  error={form.errors.medication}
                  data={medicationOptions}
                  rightSection={
                    showMedicationsLoading ? (
                      <Loader size="xs" data-testid="medication-loading" />
                    ) : (
                      <FontAwesomeIcon icon={faMagnifyingGlass} size="lg" />
                    )
                  }
                  {...form.getInputProps("medication")}
                />
              </Grid.Col>
            </Grid>

            <Grid mt="md">
              <Grid.Col>
                <Select
                  label="Condition"
                  placeholder="Look up by condition"
                  data-testid="condition-input"
                  searchable
                  withAsterisk
                  disabled={loading || !medication || conditionsLoading}
                  error={form.errors.condition}
                  data={conditionsForMedication ?? []}
                  rightSection={
                    showConditionsLoading ? (
                      <Loader size="xs" data-testid="condition-loading" />
                    ) : (
                      <FontAwesomeIcon icon={faMagnifyingGlass} size="lg" />
                    )
                  }
                  {...form.getInputProps("condition")}
                />
              </Grid.Col>
            </Grid>

            <Grid mt="md">
              <Grid.Col>
                <Select
                  label="Denial Reason"
                  placeholder="Select denial reason"
                  data-testid="denial-reason-input"
                  withAsterisk
                  disabled={loading}
                  error={form.errors.denialReason}
                  data={
                    condition === Condition.HYPOPHOSPHATASIA
                      ? hppDenialReasons
                      : Object.values(DenialReason)
                  }
                  {...form.getInputProps("denialReason")}
                />
              </Grid.Col>
            </Grid>

            <SubDenialReasonField
              condition={form.values.condition}
              denialReason={form.values.denialReason}
              subDenialReasons={form.values.subDenialReasons}
              disabled={loading}
              allowMultipleSubDenialReasons={
                isHppStrensiqEnabled && condition === Condition.HYPOPHOSPHATASIA
              }
              onChange={(values) =>
                form.setFieldValue("subDenialReasons", values)
              }
              onClear={() => form.setFieldValue("subDenialReasons", [])}
              error={form.errors.subDenialReasons}
            />

            <Grid mt="md">
              <Grid.Col>
                <Checkbox
                  label="Request expedited review"
                  description="For urgent cases. Insurers must respond within 72 hours by law."
                  data-testid="expedited-review-input"
                  checked={form.values.isExpeditedReview}
                  disabled={loading}
                  onChange={(event) => {
                    userTouchedExpedited.current = true;
                    form.setFieldValue(
                      "isExpeditedReview",
                      event.currentTarget.checked,
                    );
                  }}
                />
              </Grid.Col>
            </Grid>

            {isGlp1Condition(form.values.condition) && (
              <Grid mt="md">
                <Grid.Col>
                  <div className={styles.fdaDisclaimer}>
                    <span className={styles.checkbox}>
                      <Checkbox
                        label="This patient meets the FDA guidelines for GLP-1"
                        checked={form.values.meetsFdaGuidelines}
                        onChange={(event) =>
                          form.setFieldValue(
                            "meetsFdaGuidelines",
                            event.currentTarget.checked,
                          )
                        }
                        error={form.errors.meetsFdaGuidelines}
                        classNames={{
                          inner: styles.checkboxContainer,
                          input: styles.checkboxInput,
                          icon: styles.checkboxIcon,
                        }}
                      />
                    </span>
                    <Popover
                      withArrow
                      styles={{
                        arrow: { background: "#000" },
                        dropdown: {
                          background: "#000",
                          borderRadius: "4px",
                          padding: "8px",
                        },
                      }}
                      width="350px"
                    >
                      <Popover.Target>
                        <FontAwesomeIcon
                          color="#29A7D3"
                          icon={faInfoCircle}
                          title="Info"
                        />
                      </Popover.Target>
                      <Popover.Dropdown>
                        <FdaEligibilityPopoverContent
                          condition={
                            form.values.condition as
                              | Condition.OBESITY
                              | Condition.TYPE_2_DIABETES
                          }
                        />
                      </Popover.Dropdown>
                    </Popover>
                  </div>
                </Grid.Col>
              </Grid>
            )}
          </form>
        </div>

        {shouldShowSpecialSituations && (
          <div className={styles.rightColumnContainer}>
            <SpecialAppealSituationsCard
              selectedSituations={selectedSituations}
              onToggleSituation={toggleSituation}
              disabled={loading}
            />
          </div>
        )}
      </div>
    </Navbar>
  );
}
